import type { FileReviewDecisionLineGroup } from '@/lib/pr-v2/file-review-decisions';

import {
  observePrV2DecisionMarker,
  unobservePrV2DecisionMarker,
} from './pr-v2-decision-visibility';

import {
  formatFileReviewDecisionLabel,
  formatFileReviewDecisionLines,
  getFileReviewDecisionGlyphChar,
} from '@/lib/pr-v2/file-review-decisions';

type SyncPrV2DecisionGutterMarkersArgs = {
  shadowRoot: ShadowRoot;
  filename: string;
  groups: readonly FileReviewDecisionLineGroup[];
  onFirstHover?: (filename: string, lineStart: number, decisionCount: number) => void;
  onClick?: (payload: PrV2DecisionGutterMarkerClickPayload) => void;
};

type DecisionGutterMarkerSyncState = {
  signature: string;
  onFirstHover?: SyncPrV2DecisionGutterMarkersArgs['onFirstHover'];
  onClick?: SyncPrV2DecisionGutterMarkersArgs['onClick'];
  /** Every marker this sync drew, including ones Pierre has since removed with their rows. */
  markers: HTMLButtonElement[];
};

export type PrV2DecisionGutterMarkerClickPayload = {
  filename: string;
  lineStart: number;
  lineEnd: number;
  decisionLabel: string;
};

const DECISION_GUTTER_MARKER_STYLE_ID = 'pr-v2-decision-gutter-marker-style';
const DECISION_GUTTER_MARKER_SELECTOR = '[data-pr-v2-decision-gutter-marker]';
const DECISION_GUTTER_MARKER_HOST_SELECTOR = '[data-pr-v2-decision-gutter-marker-host]';
const decisionGutterMarkerSyncStateByShadowRoot = new WeakMap<
  ShadowRoot,
  DecisionGutterMarkerSyncState
>();

const DECISION_GUTTER_MARKER_CSS = `
[data-column-number] {
  padding-inline-start: 24px !important;
}

[data-pr-v2-decision-gutter-marker-host] {
  --pr-v2-decision-gutter-marker-hit-target: 18px;
  position: relative !important;
  overflow: visible !important;
}

[data-pr-v2-decision-gutter-marker] {
  /* Expanded-context line numbers ignore the pointer; a marker inside one must not. */
  pointer-events: auto !important;
  position: absolute !important;
  left: 3px !important;
  top: 50% !important;
  z-index: 8 !important;
  display: inline-flex !important;
  width: var(--pr-v2-decision-gutter-marker-hit-target) !important;
  height: var(--pr-v2-decision-gutter-marker-hit-target) !important;
  min-width: var(--pr-v2-decision-gutter-marker-hit-target) !important;
  min-height: var(--pr-v2-decision-gutter-marker-hit-target) !important;
  align-items: center !important;
  justify-content: center !important;
  box-sizing: border-box !important;
  transform: translateY(-50%) !important;
  border: 1px solid rgba(251, 146, 60, 0.85) !important;
  border-radius: 4px !important;
  background: rgba(251, 146, 60, 0.14) !important;
  color: #fdba74 !important;
  font: 700 12px/1 ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace !important;
  padding: 0 !important;
  margin: 0 !important;
  cursor: pointer !important;
}

[data-pr-v2-decision-gutter-marker]::after {
  content: attr(data-pr-v2-decision-tooltip);
  position: absolute !important;
  left: 0 !important;
  bottom: calc(100% + 4px) !important;
  z-index: 100 !important;
  width: max-content !important;
  max-width: 360px !important;
  box-sizing: border-box !important;
  border: 1px solid rgba(82, 82, 91, 0.9) !important;
  border-radius: 6px !important;
  background: #18181b !important;
  color: #f4f4f5 !important;
  font: 400 12px/1.35 ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif !important;
  opacity: 0 !important;
  padding: 7px 9px !important;
  pointer-events: none !important;
  text-align: left !important;
  visibility: hidden !important;
  white-space: pre-line !important;
}

[data-pr-v2-decision-gutter-marker][data-pr-v2-decision-tooltip-below]::after {
  bottom: auto !important;
  top: calc(100% + 4px) !important;
}

[data-pr-v2-decision-gutter-marker]:hover,
[data-pr-v2-decision-gutter-marker]:focus-visible {
  background: rgba(251, 146, 60, 0.22) !important;
  border-color: rgba(253, 186, 116, 0.95) !important;
  color: #fed7aa !important;
}

[data-pr-v2-decision-gutter-marker]:hover::after,
[data-pr-v2-decision-gutter-marker]:focus-visible::after {
  opacity: 1 !important;
  visibility: visible !important;
}

[data-pr-v2-decision-gutter-marker]:focus-visible {
  outline: 1px solid rgba(253, 186, 116, 0.75) !important;
  outline-offset: 2px !important;
}
`;

function ensureDecisionGutterMarkerStyle(shadowRoot: ShadowRoot): void {
  let style = shadowRoot.getElementById(DECISION_GUTTER_MARKER_STYLE_ID);
  if (!style) {
    style = document.createElement('style');
    style.id = DECISION_GUTTER_MARKER_STYLE_ID;
    shadowRoot.appendChild(style);
  }

  if (style.textContent !== DECISION_GUTTER_MARKER_CSS) {
    style.textContent = DECISION_GUTTER_MARKER_CSS;
  }
}

function removeDecisionGutterMarkerStyle(shadowRoot: ShadowRoot): void {
  shadowRoot.getElementById(DECISION_GUTTER_MARKER_STYLE_ID)?.remove();
}

function clearDecisionGutterMarkers(shadowRoot: ShadowRoot): void {
  // Unobserve what this sync drew, not just what the DOM still holds: Pierre can replace a row
  // and its marker without a sync, and the group card would keep following the detached one.
  decisionGutterMarkerSyncStateByShadowRoot
    .get(shadowRoot)
    ?.markers.forEach((marker) => unobservePrV2DecisionMarker(marker));

  shadowRoot
    .querySelectorAll<HTMLButtonElement>(DECISION_GUTTER_MARKER_SELECTOR)
    .forEach((marker) => marker.remove());

  shadowRoot
    .querySelectorAll<HTMLElement>(DECISION_GUTTER_MARKER_HOST_SELECTOR)
    .forEach((host) => host.removeAttribute('data-pr-v2-decision-gutter-marker-host'));
}

function getDecisionGutterCell(shadowRoot: ShadowRoot, lineNumber: number): HTMLElement | null {
  const candidates = Array.from(
    shadowRoot.querySelectorAll<HTMLElement>(`[data-column-number="${lineNumber}"]`),
  ).filter((cell) => cell.getAttribute('data-line-type') !== 'change-deletion');

  // Split diffs render deletion and addition gutters in DOM order. Review decisions anchor to the
  // new side, so choose the last matching non-deletion cell when both sides share a context line.
  return candidates.at(-1) ?? null;
}

const DECISION_TOOLTIP_BELOW_ATTRIBUTE = 'data-pr-v2-decision-tooltip-below';
// Used when the ::after height cannot be measured (e.g. jsdom); roughly a 3-4 line tooltip.
const DECISION_TOOLTIP_FALLBACK_HEIGHT_PX = 72;
const DECISION_TOOLTIP_CLEARANCE_PX = 8;

// The sticky file header paints above the diff body (opaque bg, z-20) and the body wrapper clips
// overflow, so an upward tooltip is invisible above the header's bottom edge — both at the top of
// the file and mid-file when the marker sits just under the pinned header.
function getDecisionTooltipObstructionBottom(marker: HTMLElement): number {
  const root = marker.getRootNode();
  const host = root instanceof ShadowRoot ? root.host : null;
  const stickyHeader = host
    ?.closest('section')
    ?.querySelector('[data-pr-v2-file-header-sticky-wrap="true"]');
  if (stickyHeader) {
    return stickyHeader.getBoundingClientRect().bottom;
  }

  // Without a header the diff body's top edge still clips the tooltip.
  return host?.getBoundingClientRect().top ?? 0;
}

// Placement is decided per hover because the obstruction depends on scroll position.
function updateDecisionTooltipPlacement(marker: HTMLElement): void {
  const markerRect = marker.getBoundingClientRect();
  const root = marker.getRootNode();
  const host = root instanceof ShadowRoot ? root.host : null;
  const tooltipHeight =
    Number.parseFloat(getComputedStyle(marker, '::after').height) ||
    DECISION_TOOLTIP_FALLBACK_HEIGHT_PX;
  const neededSpace = tooltipHeight + DECISION_TOOLTIP_CLEARANCE_PX;
  const spaceAbove = markerRect.top - getDecisionTooltipObstructionBottom(marker);
  const spaceBelow =
    (host?.getBoundingClientRect().bottom ?? Number.POSITIVE_INFINITY) - markerRect.bottom;

  if (spaceAbove < neededSpace && spaceBelow > spaceAbove) {
    marker.setAttribute(DECISION_TOOLTIP_BELOW_ATTRIBUTE, '');
  } else {
    marker.removeAttribute(DECISION_TOOLTIP_BELOW_ATTRIBUTE);
  }
}

function getDecisionGutterMarkerLabel(group: FileReviewDecisionLineGroup): string {
  return formatFileReviewDecisionLabel(group.decisions) ?? 'Review decision';
}

function getDecisionGutterMarkerTestId(filename: string, lineStart: number): string {
  return `pr-v2-decision-gutter-marker-${filename}-${lineStart}`;
}

function buildDecisionGutterMarkerSyncSignature(
  filename: string,
  groups: readonly FileReviewDecisionLineGroup[],
): string {
  return JSON.stringify(
    groups.map((group) => ({
      filename,
      lineStart: group.lineStart,
      lineEnd: group.lineEnd,
      glyph: getFileReviewDecisionGlyphChar(group.decisions),
      label: getDecisionGutterMarkerLabel(group),
    })),
  );
}

function getDirectDecisionGutterMarkers(cell: HTMLElement): HTMLButtonElement[] {
  return Array.from(cell.children).filter(
    (child): child is HTMLButtonElement =>
      child.tagName === 'BUTTON' && child.hasAttribute('data-pr-v2-decision-gutter-marker'),
  );
}

function isDecisionGutterMarkerDomCurrent({
  shadowRoot,
  filename,
  groups,
}: {
  shadowRoot: ShadowRoot;
  filename: string;
  groups: readonly FileReviewDecisionLineGroup[];
}): boolean {
  const markers = Array.from(
    shadowRoot.querySelectorAll<HTMLButtonElement>(DECISION_GUTTER_MARKER_SELECTOR),
  );
  const hosts = Array.from(
    shadowRoot.querySelectorAll<HTMLElement>(DECISION_GUTTER_MARKER_HOST_SELECTOR),
  );
  if (markers.length !== groups.length || hosts.length !== groups.length) {
    return false;
  }

  const expectedMarkers = new Set<HTMLButtonElement>();
  for (const group of groups) {
    const cell = getDecisionGutterCell(shadowRoot, group.lineStart);
    if (!cell || !cell.hasAttribute('data-pr-v2-decision-gutter-marker-host')) {
      return false;
    }

    const directMarkers = getDirectDecisionGutterMarkers(cell);
    if (directMarkers.length !== 1) {
      return false;
    }

    const marker = directMarkers[0];
    if (!marker) {
      return false;
    }

    if (
      !marker.isConnected ||
      marker.textContent !== getFileReviewDecisionGlyphChar(group.decisions) ||
      marker.getAttribute('data-pr-v2-decision-tooltip') !== getDecisionGutterMarkerLabel(group) ||
      marker.getAttribute('data-testid') !==
        getDecisionGutterMarkerTestId(filename, group.lineStart)
    ) {
      return false;
    }

    expectedMarkers.add(marker);
  }

  return markers.every((marker) => expectedMarkers.has(marker));
}

function createDecisionGutterMarker({
  filename,
  group,
  onFirstHover,
  onClick,
}: {
  filename: string;
  group: FileReviewDecisionLineGroup;
  onFirstHover?: (filename: string, lineStart: number, decisionCount: number) => void;
  onClick?: (payload: PrV2DecisionGutterMarkerClickPayload) => void;
}): HTMLButtonElement {
  const decisionLabel = getDecisionGutterMarkerLabel(group);
  const marker = document.createElement('button');
  let hasReportedHover = false;

  marker.type = 'button';
  // Shape-matched glyph: "?" = choice, "!" = consequence, "!?" = a line hosting both
  marker.textContent = getFileReviewDecisionGlyphChar(group.decisions);
  marker.setAttribute(
    'aria-label',
    `Add review decision to AI chat (${formatFileReviewDecisionLines(group.lineStart, group.lineEnd)}): ${decisionLabel}`,
  );
  marker.setAttribute('data-pr-v2-decision-tooltip', decisionLabel);
  marker.setAttribute('data-pr-v2-decision-gutter-marker', '');
  marker.setAttribute('data-testid', getDecisionGutterMarkerTestId(filename, group.lineStart));

  const reportHover = () => {
    if (hasReportedHover) {
      return;
    }

    hasReportedHover = true;
    onFirstHover?.(filename, group.lineStart, group.decisions.length);
  };

  const updateTooltipPlacement = () => updateDecisionTooltipPlacement(marker);

  marker.addEventListener('mouseenter', reportHover);
  marker.addEventListener('focus', reportHover);
  marker.addEventListener('mouseenter', updateTooltipPlacement);
  marker.addEventListener('focus', updateTooltipPlacement);
  marker.addEventListener('pointerdown', (event) => {
    event.stopPropagation();
  });
  marker.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    onClick?.({
      filename,
      lineStart: group.lineStart,
      lineEnd: group.lineEnd,
      decisionLabel,
    });
  });

  return marker;
}

/** Removes a root's markers and stops following them, for a root the diff is done with. */
export function disposePrV2DecisionGutterMarkers(shadowRoot: ShadowRoot): void {
  clearDecisionGutterMarkers(shadowRoot);
  removeDecisionGutterMarkerStyle(shadowRoot);
  decisionGutterMarkerSyncStateByShadowRoot.delete(shadowRoot);
}

export function syncPrV2DecisionGutterMarkers({
  shadowRoot,
  filename,
  groups,
  onFirstHover,
  onClick,
}: SyncPrV2DecisionGutterMarkersArgs): void {
  if (groups.length === 0) {
    disposePrV2DecisionGutterMarkers(shadowRoot);
    return;
  }

  ensureDecisionGutterMarkerStyle(shadowRoot);
  const syncState: DecisionGutterMarkerSyncState = {
    signature: buildDecisionGutterMarkerSyncSignature(filename, groups),
    onFirstHover,
    onClick,
    markers: [],
  };
  const previousSyncState = decisionGutterMarkerSyncStateByShadowRoot.get(shadowRoot);
  if (
    previousSyncState?.signature === syncState.signature &&
    previousSyncState.onFirstHover === onFirstHover &&
    previousSyncState.onClick === onClick &&
    isDecisionGutterMarkerDomCurrent({ shadowRoot, filename, groups })
  ) {
    return;
  }

  clearDecisionGutterMarkers(shadowRoot);

  groups.forEach((group) => {
    const cell = getDecisionGutterCell(shadowRoot, group.lineStart);
    if (!cell) {
      return;
    }

    cell.setAttribute('data-pr-v2-decision-gutter-marker-host', '');
    const marker = createDecisionGutterMarker({ filename, group, onFirstHover, onClick });
    cell.appendChild(marker);
    observePrV2DecisionMarker(marker, filename, group.lineStart);
    syncState.markers.push(marker);
  });

  if (syncState.markers.length === 0) {
    removeDecisionGutterMarkerStyle(shadowRoot);
    decisionGutterMarkerSyncStateByShadowRoot.delete(shadowRoot);
    return;
  }

  decisionGutterMarkerSyncStateByShadowRoot.set(shadowRoot, syncState);
}
